<template>
  <div class="space-y-8">
    <div class="border-b border-b-gray-100 pb-4">
      <h1 class="text-xl text-gray-900">Analytics</h1>
      <p class="text-md font-light text-gray-500 mt-1">Organization analytics</p>
    </div>

    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
      <div class="flex items-baseline gap-2">
        <span class="text-5xl font-bold text-gray-900">{{ formatNumber(totalUsage) }}</span>
        <span class="text-md font-light text-gray-500">total requests</span>
      </div>

      <div class="flex flex-col items-end gap-2">
        <div class="flex bg-gray-100 p-1 rounded-lg">
          <button v-for="period in periods" :key="period.value" @click="selectPeriod(period.value)"
            class="px-3 py-1 text-sm font-medium rounded-md transition-all duration-200"
            :class="selectedPeriod === period.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'">
            {{ period.label }}
          </button>
        </div>

        <div v-if="selectedPeriod === 'custom'"
          class="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-md border border-gray-200 text-sm animate-in fade-in slide-in-from-top-1 duration-200">
          <input type="date" v-model="startDateInput"
            class="bg-transparent border-none p-0 text-gray-700 focus:ring-0 text-sm" :max="endDateInput">
          <span class="text-gray-400">to</span>
          <input type="date" v-model="endDateInput"
            class="bg-transparent border-none p-0 text-gray-700 focus:ring-0 text-sm" :min="startDateInput">
        </div>

        <div v-else class="text-xs text-gray-500 font-medium px-1 h-[38px] flex items-center">
          {{ formattedDateRange }}
        </div>

        <span class="text-[10px] text-gray-400 font-light mr-1">
          Data updates daily
        </span>
      </div>
    </div>

    <div v-if="isLoading" class="py-12 text-center text-gray-400 animate-pulse">
      Loading analytics data...
    </div>

    <div v-else-if="error" class="bg-red-50 text-red-600 p-4 rounded-md text-sm">
      {{ error }}
    </div>

    <div v-else class="bg-white rounded-lg border border-gray-100 p-6 shadow-sm">
      <h3 class="text-sm font-medium text-gray-500 mb-6">Daily Usage (UTC)</h3>

      <div class="h-64 flex flex-col">
        <div class="flex-1 flex items-end justify-between gap-1 border-b border-gray-100 pb-2">
          <div v-for="day in chartData" :key="day.dateStr"
            class="flex-1 h-full flex flex-col justify-end items-center group/bar relative">
            <div
              class="opacity-0 group-hover/bar:opacity-100 absolute bottom-full mb-2 z-10 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none transition-opacity shadow-sm">
              <div class="font-semibold">{{ day.humanDate }}</div>
              <div>{{ formatNumber(day.count) }} requests</div>
              <div class="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900">
              </div>
            </div>

            <div class="w-full flex-1 flex flex-col justify-end items-center">
              <div v-if="day.count > 0"
                class="w-full max-w-[32px] bg-gray-800 rounded-t hover:bg-gray-700 transition-colors duration-200 min-h-[4px]"
                :style="{ height: `${day.heightPercentage}%` }"></div>
              <div v-else class="w-full max-w-[32px] h-[1px] bg-gray-200"></div>
            </div>
          </div>
        </div>

        <div class="mt-2 flex justify-between text-[10px] text-gray-400 px-1">
          <span>{{ chartData[0]?.shortDate }}</span>
          <span>{{ chartData[chartData.length - 1]?.shortDate }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { api } from '@/services/apiClient';

// --- Configuration ---
const periods = [
  { label: '1d', value: '1d' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
  { label: 'M', value: 'm' },
  { label: 'Custom', value: 'custom' },
];

// --- Helper to calculate UTC date string with offset ---
const getUtcDateStr = (offsetDays = 0) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().split('T')[0];
};

// --- State ---
const route = useRoute();
const isLoading = ref(false);
const error = ref(null);
const usages = ref([]);
const selectedPeriod = ref('30d');

// Default Initialization (Matches 30d logic: UTC-31 to UTC-1)
const startDateInput = ref(getUtcDateStr(-31));
const endDateInput = ref(getUtcDateStr(-1));

// --- Computeds ---

const periodStart = computed(() => {
  const date = new Date(startDateInput.value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
});

const periodEnd = computed(() => {
  const date = new Date(endDateInput.value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999);
});

const formattedDateRange = computed(() => {
  const start = new Date(periodStart.value);
  const end = new Date(periodEnd.value);
  const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  return `${fmt.format(start)} – ${fmt.format(end)}`;
});

const totalUsage = computed(() => {
  return usages.value.reduce((sum, u) => sum + (u.count || 0), 0);
});

const chartData = computed(() => {
  if (!startDateInput.value || !endDateInput.value) return [];

  // 1. Group raw usages by Date
  const usageMap = new Map();
  usages.value.forEach(u => {
    const dateKey = new Date(u.periodStart).toISOString().split('T')[0];
    const current = usageMap.get(dateKey) || 0;
    usageMap.set(dateKey, current + u.count);
  });

  // 2. Build continuous timeline
  const days = [];
  let current = new Date(periodStart.value);
  const end = new Date(periodEnd.value);

  let maxCount = 0;

  while (current <= end) {
    const dateStr = current.toISOString().split('T')[0];
    const count = usageMap.get(dateStr) || 0;
    if (count > maxCount) maxCount = count;

    days.push({
      dateStr,
      timestamp: current.getTime(),
      count,
      humanDate: new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', dateStyle: 'medium' }).format(current),
      shortDate: new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(current)
    });

    current.setUTCDate(current.getUTCDate() + 1);
  }

  // 3. Calculate percentages
  return days.map(d => ({
    ...d,
    heightPercentage: maxCount > 0 ? (d.count / maxCount) * 100 : 0
  }));
});

// --- Methods ---

const formatNumber = (num) => new Intl.NumberFormat('en-US').format(num);

const selectPeriod = (period) => {
  selectedPeriod.value = period;
  if (period === 'custom') return;

  // Always end yesterday (UTC-1)
  const endStr = getUtcDateStr(-1);
  let startStr = '';

  const today = new Date(); // Helper for month calculation

  switch (period) {
    case '1d':
      // UTC-2 to UTC-1 (2 days)
      startStr = getUtcDateStr(-2);
      break;
    case '7d':
      // UTC-8 to UTC-1 (8 days)
      startStr = getUtcDateStr(-8);
      break;
    case '30d':
      // UTC-31 to UTC-1 (31 days)
      startStr = getUtcDateStr(-31);
      break;
    case 'm':
      // 1st of Current Month (UTC)
      const startOfMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      startStr = startOfMonth.toISOString().split('T')[0];
      break;
  }

  // Update inputs
  startDateInput.value = startStr;
  endDateInput.value = endStr;
};

const fetchUsages = async () => {
  const orgId = route.params.id;
  if (!orgId) return;

  isLoading.value = true;
  error.value = null;

  try {
    const endpoint = `/organizations/${orgId}/usages`;
    const params = new URLSearchParams({
      period_start: periodStart.value.toString(),
      period_end: periodEnd.value.toString()
    });

    const res = await api.get(`${endpoint}?${params.toString()}`);
    usages.value = res.data.usages || [];
  } catch (err) {
    console.error(err);
    error.value = 'Failed to load usage data.';
  } finally {
    isLoading.value = false;
  }
};

// --- Lifecycle ---

onMounted(fetchUsages);

watch([periodStart, periodEnd], fetchUsages);
</script>